// ==============================|| PHARMA CHANGE CONTROL & VERSIONING WORKFLOW ||============================== //
// Aligned with 21 CFR Part 11 Compliance for Batch Processing System

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * RECIPE VERSIONING WORKFLOW
 * ═══════════════════════════════════════════════════════════════════════════════
 * Purpose: Manage master recipe changes, formula updates, and process modifications
 * Compliance: 21 CFR Part 11 - Electronic Records & Electronic Signatures
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export const RECIPE_VERSIONING_WORKFLOW = [
  {
    stage: 1,
    stageName: "Change Request & Initiation",
    stageCode: "INIT",
    description: "Formal request for recipe modification with detailed justification",

    keyActivities: [
      "Submit Change Request Form with detailed justification",
      "Document reason for change (formulation improvement, quality issue, cost optimization, regulatory requirement)",
      "Identify affected products, batches, and processes",
      "Attach supporting documentation (lab reports, customer complaints, regulatory guidelines)",
      "Preliminary risk assessment (high/medium/low impact)",
      "Define scope of change (materials, process parameters, equipment, packaging)"
    ],

    stakeholders: [
      { role: "Change Initiator", responsibility: "R&D / Production / QA", action: "Submits change request" },
      { role: "Technical Owner", responsibility: "R&D Manager / Production Manager", action: "Reviews feasibility" },
      { role: "Document Controller", responsibility: "QA Documentation", action: "Assigns change control number" }
    ],

    output: {
      document: "Change Request Form (CR-XXXX)",
      status: "Draft",
      electronicSignature: "Initiator e-signature required",
      auditTrail: "Timestamp, user ID, reason for change logged"
    },

    triggerForNext: [
      "Change Request approved by Technical Owner",
      "Change Control number assigned",
      "All mandatory fields completed in CR form"
    ],

    cfr21Compliance: [
      "Electronic signature of initiator (§11.50)",
      "Audit trail of submission (§11.10)",
      "Unique identifier (CR number) for traceability"
    ]
  },

  {
    stage: 2,
    stageName: "Impact Assessment & Risk Analysis",
    stageCode: "IMPACT",
    description: "Comprehensive evaluation of change impact on quality, safety, and compliance",

    keyActivities: [
      "Conduct formal Impact Assessment (IA)",
      "Quality Impact: Effect on CQAs (Critical Quality Attributes)",
      "Regulatory Impact: Need for regulatory filing (PAS, CBE, Prior Approval)",
      "Validation Impact: Re-validation requirements for equipment/processes",
      "Supply Chain Impact: Material sourcing, vendor changes",
      "Cost-Benefit Analysis",
      "Risk categorization using FMEA or similar methodology",
      "Identify affected SOPs, batch records, specifications",
      "Determine if stability studies or bioequivalence studies required"
    ],

    stakeholders: [
      { role: "QA Lead", responsibility: "Quality Assurance", action: "Assesses quality & regulatory impact" },
      { role: "Production Head", responsibility: "Manufacturing", action: "Evaluates manufacturing feasibility" },
      { role: "R&D Scientist", responsibility: "Research & Development", action: "Provides technical impact data" },
      { role: "Validation Team", responsibility: "Validation", action: "Determines validation needs" },
      { role: "Regulatory Affairs", responsibility: "RA Department", action: "Identifies regulatory filing requirements" }
    ],

    output: {
      document: "Impact Assessment Report (IA-XXXX)",
      riskLevel: "High / Medium / Low",
      status: "Under Review",
      electronicSignature: "QA Lead, Production Head, R&D e-signatures required",
      auditTrail: "All assessment inputs and decisions logged with timestamps"
    },

    triggerForNext: [
      "Impact Assessment completed and signed by all stakeholders",
      "Risk level determined and mitigation plan defined",
      "Go/No-Go decision made by Change Control Board (CCB)"
    ],

    cfr21Compliance: [
      "Multi-level electronic signatures (§11.50)",
      "Complete audit trail of risk assessment (§11.10)",
      "Document retention per 21 CFR 211.180"
    ]
  },

  {
    stage: 3,
    stageName: "Recipe Development & Testing",
    stageCode: "DEV",
    description: "Create new recipe version, conduct trials, and validate changes",

    keyActivities: [
      "Create new recipe version in system (Version X.Y)",
      "Update Master Formula Record (MFR)",
      "Modify process parameters, materials, or procedures as per CR",
      "Conduct lab-scale or pilot-scale trials",
      "Execute trial batches with modified recipe",
      "Collect data: yield, quality parameters, in-process controls",
      "Compare new version with baseline (previous version)",
      "Document deviations or observations during trials",
      "QA review of trial batch data and analytical results",
      "Stability testing initiation (if required)",
      "Update associated documents: SOPs, work instructions, specifications"
    ],

    stakeholders: [
      { role: "R&D Team", responsibility: "Research & Development", action: "Develops new recipe version" },
      { role: "Production Operators", responsibility: "Manufacturing", action: "Executes trial batches" },
      { role: "QC Analysts", responsibility: "Quality Control Lab", action: "Performs analytical testing" },
      { role: "QA Reviewer", responsibility: "Quality Assurance", action: "Reviews trial data & documentation" },
      { role: "Process Engineer", responsibility: "Engineering", action: "Validates process parameters" }
    ],

    output: {
      document: "Recipe Version X.Y (Draft)",
      trialBatchReports: "Trial Batch Report (TBR-XXXX)",
      analyticalData: "COA (Certificate of Analysis) for trial batches",
      status: "In Development",
      electronicSignature: "R&D Lead, QC Manager, QA Reviewer e-signatures",
      auditTrail: "Version history, trial data, deviations logged"
    },

    triggerForNext: [
      "Trial batches meet acceptance criteria",
      "All quality parameters within specification",
      "QA approves trial batch documentation",
      "No critical deviations observed or all deviations resolved"
    ],

    cfr21Compliance: [
      "Version control with complete change history (§11.10)",
      "Electronic signatures on trial batch records (§11.50)",
      "Secure, time-stamped audit trail (§11.10(e))"
    ]
  },

  {
    stage: 4,
    stageName: "QA Review & Approval",
    stageCode: "QA_REVIEW",
    description: "Quality Assurance formal review and approval of new recipe version",

    keyActivities: [
      "QA comprehensive review of all documentation",
      "Verify compliance with cGMP and regulatory requirements",
      "Review trial batch records, deviations, and analytical data",
      "Confirm all SOPs, specifications, and BMRs updated",
      "Check electronic signatures and audit trails",
      "Verify training completion for impacted personnel",
      "Ensure all change control documents complete",
      "Final risk assessment review",
      "QA Head approval for production rollout",
      "Confirm regulatory filing status (if applicable)"
    ],

    stakeholders: [
      { role: "QA Manager", responsibility: "Quality Assurance", action: "Reviews all documentation for compliance" },
      { role: "QA Head", responsibility: "QA Leadership", action: "Final approval authority" },
      { role: "Document Control", responsibility: "QA Documentation", action: "Ensures document integrity" },
      { role: "Training Coordinator", responsibility: "Training Dept", action: "Confirms training completion" }
    ],

    output: {
      document: "QA Approval Report",
      status: "QA Approved",
      electronicSignature: "QA Manager and QA Head e-signatures required (dual approval)",
      auditTrail: "QA review comments, approval timestamp, decision rationale logged",
      certificateOfApproval: "Recipe Version X.Y approved for production"
    },

    triggerForNext: [
      "QA Head provides electronic signature approval",
      "All documentation reviewed and accepted",
      "Training completion verified for all impacted staff",
      "No open deviations or CAPAs related to change"
    ],

    cfr21Compliance: [
      "Dual electronic signatures for critical approval (§11.50, §11.200)",
      "Authority checks and role-based access (§11.10(g))",
      "Complete and unalterable audit trail (§11.10(e))"
    ]
  },

  {
    stage: 5,
    stageName: "Production Release & Monitoring",
    stageCode: "RELEASE",
    description: "Final release of recipe to production and post-implementation monitoring",

    keyActivities: [
      "Activate new recipe version in production system",
      "Archive previous recipe version (maintain for reference)",
      "Communicate change to all relevant departments",
      "Execute first production batches using new recipe",
      "Enhanced monitoring of initial production batches (3-5 batches)",
      "Track KPIs: yield, quality, cycle time, deviations",
      "Post-implementation review after defined period (e.g., 90 days)",
      "Collect feedback from production, QC, and QA",
      "Confirm stability data (ongoing)",
      "Close change control with final report",
      "Update master document repository"
    ],

    stakeholders: [
      { role: "Production Manager", responsibility: "Manufacturing", action: "Deploys new recipe in production" },
      { role: "QA Monitor", responsibility: "Quality Assurance", action: "Monitors initial batches" },
      { role: "QC Lab", responsibility: "Quality Control", action: "Tests production batches" },
      { role: "Change Owner", responsibility: "Original Initiator", action: "Tracks effectiveness" },
      { role: "Document Control", responsibility: "QA Documentation", action: "Archives change control package" }
    ],

    output: {
      document: "Production Release Certificate",
      status: "Active in Production",
      electronicSignature: "Production Manager, QA Monitor e-signatures",
      postImplementationReport: "Change Effectiveness Report (after 90 days)",
      auditTrail: "Production batches using new version, monitoring data logged",
      archivalRecord: "Complete change control package archived per retention policy"
    },

    triggerForNext: [
      "Not applicable - Final stage",
      "Change control officially closed after post-implementation review",
      "All documentation archived and retrievable for audits"
    ],

    cfr21Compliance: [
      "Electronic record retention per §11.10 and 21 CFR 211.180",
      "Records protected from alteration after finalization (§11.10(c))",
      "Audit trail available for entire change lifecycle (§11.10(e))",
      "Role-based access and signature manifestations (§11.50, §11.200)"
    ]
  }
];

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * STANDARD BLOCK VERSIONING WORKFLOW
 * ═══════════════════════════════════════════════════════════════════════════════
 * Purpose: Manage reusable process templates and standard operating procedures
 * Compliance: 21 CFR Part 11 - Electronic Records & Electronic Signatures
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export const STANDARD_BLOCK_VERSIONING_WORKFLOW = [
  {
    stage: 1,
    stageName: "Block Change Request & Justification",
    stageCode: "BLOCK_INIT",
    description: "Request for modification of standard block template with business justification",

    keyActivities: [
      "Submit Standard Block Change Request Form",
      "Identify standard block to be modified (ID, Name, Version)",
      "Document reason for change (process improvement, safety, efficiency, regulatory)",
      "List all recipes/products using this standard block",
      "Assess impact scope (number of affected recipes and batches)",
      "Preliminary feasibility check",
      "Assign priority level (critical/high/medium/low)",
      "Attach supporting evidence or references"
    ],

    stakeholders: [
      { role: "Process Engineer", responsibility: "Process Engineering", action: "Initiates block change request" },
      { role: "Production Supervisor", responsibility: "Manufacturing", action: "Provides operational input" },
      { role: "Technical Lead", responsibility: "R&D / Engineering", action: "Reviews technical feasibility" },
      { role: "QA Representative", responsibility: "Quality Assurance", action: "Preliminary compliance check" }
    ],

    output: {
      document: "Standard Block Change Request (SBCR-XXXX)",
      status: "Initiated",
      electronicSignature: "Process Engineer e-signature",
      impactSummary: "List of affected recipes and estimated impact",
      auditTrail: "Submission timestamp, user, justification logged"
    },

    triggerForNext: [
      "Change request approved by Technical Lead",
      "Impact scope assessment completed",
      "Change control number assigned for tracking"
    ],

    cfr21Compliance: [
      "Electronic signature of initiator (§11.50)",
      "Unique identifier for traceability (SBCR number)",
      "Audit trail of submission and initial review (§11.10)"
    ]
  },

  {
    stage: 2,
    stageName: "Cross-Impact Analysis & Validation Planning",
    stageCode: "BLOCK_IMPACT",
    description: "Analyze impact across all recipes using the block and plan validation activities",

    keyActivities: [
      "Generate list of all recipes using the standard block",
      "Assess impact on each affected recipe (quality, process, equipment)",
      "Determine if recipe re-approval required for each affected item",
      "Risk assessment: FMEA for standard block change",
      "Identify validation/re-validation requirements",
      "Assess training needs for modified procedures",
      "Review regulatory implications (change notification requirements)",
      "Develop validation protocol (if applicable)",
      "Create implementation plan with timeline",
      "Cost-benefit analysis for large-scale changes"
    ],

    stakeholders: [
      { role: "QA Lead", responsibility: "Quality Assurance", action: "Leads cross-impact analysis" },
      { role: "Validation Manager", responsibility: "Validation Team", action: "Plans validation activities" },
      { role: "Production Planning", responsibility: "Manufacturing", action: "Assesses production schedule impact" },
      { role: "R&D Scientist", responsibility: "Research & Development", action: "Evaluates technical impact on products" },
      { role: "Regulatory Affairs", responsibility: "RA Team", action: "Determines regulatory filing needs" }
    ],

    output: {
      document: "Cross-Impact Analysis Report (CIAR-XXXX)",
      affectedRecipesList: "List of all recipes requiring update/re-approval",
      validationPlan: "Validation Protocol (if applicable)",
      status: "Impact Assessment Complete",
      electronicSignature: "QA Lead, Validation Manager e-signatures",
      auditTrail: "All impact data, risk scores, decisions logged"
    },

    triggerForNext: [
      "Cross-impact analysis approved by stakeholders",
      "Validation plan approved (if required)",
      "Go decision from Change Control Board or management",
      "Resources allocated for implementation"
    ],

    cfr21Compliance: [
      "Multi-stakeholder electronic signatures (§11.50)",
      "Risk assessment documented with audit trail (§11.10)",
      "Validation protocol per 21 CFR 211.100(a)"
    ]
  },

  {
    stage: 3,
    stageName: "Block Modification & Validation Execution",
    stageCode: "BLOCK_DEV",
    description: "Modify standard block, execute validation, and test with affected recipes",

    keyActivities: [
      "Create new version of standard block (Version X.Y)",
      "Implement modifications to block template",
      "Update associated SOPs, work instructions, and training materials",
      "Execute validation protocol (IQ/OQ/PQ if equipment-related)",
      "Perform qualification testing on modified block",
      "Pilot test with representative recipes",
      "Execute test batches using modified standard block",
      "Collect process data and quality parameters",
      "Document any deviations or observations",
      "QA review of validation data and test results",
      "Update block documentation and version history"
    ],

    stakeholders: [
      { role: "Process Engineer", responsibility: "Process Engineering", action: "Modifies standard block" },
      { role: "Validation Specialist", responsibility: "Validation Team", action: "Executes validation protocol" },
      { role: "Production Team", responsibility: "Manufacturing", action: "Performs test batches" },
      { role: "QC Analysts", responsibility: "Quality Control", action: "Tests samples from trial batches" },
      { role: "QA Reviewer", responsibility: "Quality Assurance", action: "Reviews validation and test data" },
      { role: "Training Coordinator", responsibility: "Training", action: "Develops training for new block version" }
    ],

    output: {
      document: "Standard Block Version X.Y (Draft)",
      validationReport: "Validation Report (VR-XXXX) with IQ/OQ/PQ results",
      testBatchData: "Test Batch Records and analytical results",
      status: "Validation Complete - Pending Approval",
      electronicSignature: "Process Engineer, Validation Specialist, QA Reviewer e-signatures",
      auditTrail: "All modifications, test data, validation results logged with timestamps"
    },

    triggerForNext: [
      "Validation protocol successfully executed and approved",
      "Test batches meet all acceptance criteria",
      "No critical deviations or all deviations resolved and justified",
      "Training materials prepared and reviewed"
    ],

    cfr21Compliance: [
      "Version control with complete change history (§11.10)",
      "Electronic signatures on validation records (§11.50)",
      "Validation per 21 CFR 211.100 and 211.160",
      "Secure audit trail for all testing and validation (§11.10(e))"
    ]
  },

  {
    stage: 4,
    stageName: "QA Approval & Recipe Cascade Update",
    stageCode: "BLOCK_QA_APPROVAL",
    description: "QA approval of new block version and cascading updates to affected recipes",

    keyActivities: [
      "QA comprehensive review of modified standard block",
      "Review validation report and test batch documentation",
      "Verify cGMP and regulatory compliance",
      "Approve new standard block version for use",
      "Cascade update to all affected recipes",
      "Generate updated recipe versions incorporating new block",
      "Trigger re-approval workflow for high-impact recipes (if required)",
      "Auto-update low-impact recipes with change notification",
      "Verify training completion for all impacted personnel",
      "QA Head final approval for production deployment",
      "Update master document repository and version control system"
    ],

    stakeholders: [
      { role: "QA Manager", responsibility: "Quality Assurance", action: "Reviews block documentation for compliance" },
      { role: "QA Head", responsibility: "QA Leadership", action: "Provides final approval" },
      { role: "Document Controller", responsibility: "QA Documentation", action: "Manages cascade updates to recipes" },
      { role: "System Administrator", responsibility: "IT/System Owner", action: "Deploys block version in system" },
      { role: "Training Manager", responsibility: "Training Dept", action: "Confirms training rollout completion" }
    ],

    output: {
      document: "QA Approval Certificate for Standard Block Version X.Y",
      cascadeReport: "Recipe Cascade Update Report (lists all updated recipes)",
      status: "QA Approved - Ready for Production",
      electronicSignature: "QA Manager and QA Head dual e-signatures",
      updatedRecipes: "All affected recipe versions automatically incremented",
      auditTrail: "QA review, cascade logic, approval decisions logged",
      trainingRecords: "Training completion records for all users"
    },

    triggerForNext: [
      "QA Head electronic signature approval obtained",
      "All affected recipes successfully updated in system",
      "Training verified for 100% of impacted staff",
      "No open CAPAs or deviations related to the change"
    ],

    cfr21Compliance: [
      "Dual electronic signatures for critical approval (§11.50, §11.200)",
      "Authority checks and role-based access control (§11.10(g))",
      "Cascade audit trail showing all recipe updates (§11.10(e))",
      "Training records per 21 CFR 211.25"
    ]
  },

  {
    stage: 5,
    stageName: "Production Deployment & Post-Change Monitoring",
    stageCode: "BLOCK_RELEASE",
    description: "Deploy new block version to production and monitor effectiveness across recipes",

    keyActivities: [
      "Activate new standard block version in production system",
      "Archive previous block version (read-only for reference)",
      "Communicate change to all production sites and departments",
      "Monitor initial production batches using updated recipes (across all affected products)",
      "Track performance metrics: process efficiency, quality parameters, deviations",
      "Collect user feedback from production floor and QC lab",
      "Trend analysis of batches using new block version",
      "Post-implementation review after defined period (e.g., 3 months)",
      "Assess change effectiveness and benefits realization",
      "Document lessons learned",
      "Close change control with final effectiveness report",
      "Archive complete change control package per retention policy"
    ],

    stakeholders: [
      { role: "Production Manager", responsibility: "Manufacturing", action: "Deploys block in production across sites" },
      { role: "QA Monitor", responsibility: "Quality Assurance", action: "Monitors batch quality and compliance" },
      { role: "Process Engineer", responsibility: "Engineering", action: "Tracks process performance and efficiency" },
      { role: "Change Owner", responsibility: "Original Initiator", action: "Evaluates change effectiveness" },
      { role: "QC Manager", responsibility: "Quality Control", action: "Reviews analytical trends" },
      { role: "Document Control", responsibility: "QA Documentation", action: "Archives change control documentation" }
    ],

    output: {
      document: "Production Release Certificate for Standard Block Version X.Y",
      status: "Active in Production",
      electronicSignature: "Production Manager, QA Monitor e-signatures",
      monitoringReport: "Post-Implementation Monitoring Report (batches, KPIs, trends)",
      effectivenessReport: "Change Effectiveness Report (after 3 months)",
      auditTrail: "All production batches using new block version logged with performance data",
      archivalRecord: "Complete change control package with retention metadata",
      closureReport: "Final Change Control Closure Report"
    },

    triggerForNext: [
      "Not applicable - Final stage",
      "Change control officially closed after effectiveness review",
      "All records archived per 21 CFR 211.180 retention requirements",
      "System ready for next change cycle"
    ],

    cfr21Compliance: [
      "Electronic record retention per §11.10 and 21 CFR 211.180 (minimum 1 year after expiry)",
      "Records protected from unauthorized alteration (§11.10(c))",
      "Complete audit trail for block lifecycle accessible (§11.10(e))",
      "Role-based access maintained post-deployment (§11.10(g))",
      "Electronic signatures with manifestation in human-readable form (§11.50(b))"
    ]
  }
];

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * WORKFLOW SUMMARY TABLES
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export const RECIPE_WORKFLOW_SUMMARY = {
  totalStages: 5,
  averageDuration: "30-90 days",
  criticalApprovals: ["QA Manager (Stage 4)", "QA Head (Stage 4)", "Production Manager (Stage 5)"],
  mandatoryElectronicSignatures: 8,
  auditTrailPoints: 15,
  regulatoryAlignment: "21 CFR Part 11, 21 CFR 211 (cGMP), ICH Q10",
  keyMilestones: [
    "Change Request Approval",
    "Impact Assessment Complete",
    "Trial Batch Success",
    "QA Head Approval",
    "Production Release"
  ]
};

export const STANDARD_BLOCK_WORKFLOW_SUMMARY = {
  totalStages: 5,
  averageDuration: "45-120 days",
  criticalApprovals: ["QA Manager (Stage 4)", "QA Head (Stage 4)", "Production Manager (Stage 5)"],
  mandatoryElectronicSignatures: 10,
  auditTrailPoints: 18,
  regulatoryAlignment: "21 CFR Part 11, 21 CFR 211 (cGMP), ICH Q10",
  cascadeImpact: "Automatic versioning of all recipes using the modified block",
  keyMilestones: [
    "Block Change Request Approval",
    "Cross-Impact Analysis Complete",
    "Validation Execution Success",
    "QA Head Approval & Cascade Update",
    "Production Deployment Across Sites"
  ]
};

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * APPROVAL MATRIX - QUICK REFERENCE
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export const APPROVAL_MATRIX = {
  recipe_versioning: {
    stage1_init: ["Change Initiator", "Technical Owner"],
    stage2_impact: ["QA Lead", "Production Head", "R&D Scientist", "Validation Team", "Regulatory Affairs"],
    stage3_dev: ["R&D Lead", "QC Manager", "QA Reviewer"],
    stage4_qa_review: ["QA Manager", "QA Head"], // CRITICAL DUAL APPROVAL
    stage5_release: ["Production Manager", "QA Monitor"]
  },
  standard_block_versioning: {
    stage1_block_init: ["Process Engineer", "Technical Lead"],
    stage2_block_impact: ["QA Lead", "Validation Manager", "Regulatory Affairs"],
    stage3_block_dev: ["Process Engineer", "Validation Specialist", "QA Reviewer"],
    stage4_block_qa_approval: ["QA Manager", "QA Head"], // CRITICAL DUAL APPROVAL
    stage5_block_release: ["Production Manager", "QA Monitor"]
  }
};
